import { parseRoundFeed, type Round, type RoundFeed } from './model';
import { isShortRoundTitle, normaliseEventId } from './round-index';
import { readWithinTimeout, shortMarketSource, useShortRoundsFeed } from './use-rounds-feed';
import { useQuery, type Query, type QueryClient } from '@tanstack/react-query';

/** A round and the timezone its times are printed in. */
export type ShortRoundInfo = { round: Round; timezone: string };
/** Normalised event id -> id of the session file (one night of rounds) that lists it. */
export type ArchiveIndex = Map<string, string>;

// A session id becomes a file name: plain characters only, so an index entry never reaches another path.
const SESSION_ID = /^[A-Za-z0-9][A-Za-z0-9_-]{0,79}$/;

/**
 * Strict: any malformed field, or a session id that is not a plain file name, rejects the whole index
 * (the engine writes it whole; a bad entry means the schema moved). Event ids are normalised.
 */
export const parseArchiveIndex = (value: unknown): ArchiveIndex => {
  const data = value as { updatedAt?: unknown; events?: unknown } | null;
  const events = data?.events;
  if (
    !data ||
    typeof data !== 'object' ||
    typeof data.updatedAt !== 'number' ||
    !Number.isFinite(data.updatedAt) ||
    !events ||
    typeof events !== 'object' ||
    Array.isArray(events)
  )
    throw new Error('Invalid archive index');
  const index: ArchiveIndex = new Map();
  for (const [eventId, sessionId] of Object.entries(events)) {
    const key = normaliseEventId(eventId);
    if (!key || typeof sessionId !== 'string' || !SESSION_ID.test(sessionId)) throw new Error('Invalid archive index entry');
    index.set(key, sessionId);
  }
  return index;
};

export type ArchiveSource = {
  /** index.json: which session file holds a past round's event. */
  index(signal: AbortSignal): Promise<ArchiveIndex>;
  /** sessions/<id>.json: the rounds of one past night. */
  session(sessionId: string, signal: AbortSignal): Promise<RoundFeed>;
};

/** Static files next to rounds.json (same origin, same path rule as createShortMarketSource). */
export const createArchiveSource = (base: string): ArchiveSource => {
  if (!base.startsWith('/') || base.startsWith('//') || /[\\?#\s]/.test(base))
    throw new Error('Short-market API must be a same-origin path');
  const root = base.replace(/\/$/, '');
  const get = async (path: string, signal: AbortSignal): Promise<unknown> => {
    const response = await fetch(`${root}${path}`, { signal, cache: 'no-store', headers: { Accept: 'application/json' } });
    if (!response.ok)
      throw Object.assign(new Error(`Short-market archive unavailable (HTTP ${response.status})`), {
        status: response.status,
      });
    return response.json();
  };
  return {
    index: async signal => parseArchiveIndex(await get('/index.json', signal)),
    session: async (sessionId, signal) => {
      if (!SESSION_ID.test(sessionId)) throw new Error('Invalid archive session id');
      return parseRoundFeed(await get(`/sessions/${sessionId}.json`, signal));
    },
  };
};

/**
 * The archive source for a tenant path, or undefined when the feed is off, the path is unset, or the
 * path is invalid. Never throws: this module is imported by home cards and wallet rows, so a throw at
 * import would blank those pages. The path rule is a copy of createShortMarketSource's (api.ts); if the
 * two ever disagree, the archive turns itself off instead of crashing. Exported for tests.
 */
export const configureArchive = (base: string | undefined, feedOn: boolean): ArchiveSource | undefined => {
  if (!feedOn || !base) return undefined;
  try {
    return createArchiveSource(base);
  } catch (error) {
    console.warn('short-markets: archive path is not a same-origin path; archive off', base, error);
    return undefined;
  }
};
/** The archive of this tenant build; undefined when the feed or the archive path is off. */
export const archiveSource = configureArchive(import.meta.env.VITE_SHORT_MARKETS_API_PATH, !!shortMarketSource);

/** Index and session files are static: a good read stays for a long time, a failed read is tried again after a minute. */
const INDEX_FRESH_MS = 10 * 60_000;
const SESSION_FRESH_MS = 30 * 60_000;
const MISS_RETRY_MS = 60_000;

// One debug line per file: a missing file is an ordinary answer (a night older than the archive), not an alarm.
const noted = new Set<string>();
const readOrNull = async <T>(path: string, signal: AbortSignal, read: () => Promise<T>): Promise<T | null> => {
  try {
    return await read();
  } catch (error) {
    if (signal.aborted) throw error;
    if (!noted.has(path)) {
      noted.add(path);
      console.debug('short-markets: archive file unavailable, its rounds show no summary line', path, error);
    }
    return null;
  }
};
// A failed read resolves to null, so react-query holds it as an answer for MISS_RETRY_MS: rows that mount in the
// meantime do not ask again, and nothing retries in a loop. No retry, no refocus refetch: the files are static.
const freshness =
  <T>(fresh: number) =>
  (query: Query<T | null>) =>
    query.state.data ? fresh : MISS_RETRY_MS;
const staticFile = { retry: false as const, refetchOnWindowFocus: false, refetchOnReconnect: false };

// The event gate waits for this read: it settles within FEED_TIMEOUT_MS, whatever the request does.
const archiveIndexQueryOptions = (source: ArchiveSource) => ({
  queryKey: ['ShortRoundsArchive', 'index'],
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    readOrNull('index.json', signal, () => readWithinTimeout(signal, s => source.index(s))),
  staleTime: freshness<ArchiveIndex>(INDEX_FRESH_MS),
  // Keep the static file as long as it counts as fresh (default gcTime is 5 min).
  gcTime: INDEX_FRESH_MS,
  ...staticFile,
});
const archiveSessionQueryOptions = (source: ArchiveSource, sessionId: string | undefined) => ({
  queryKey: ['ShortRoundsArchive', 'session', sessionId],
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    readOrNull(`sessions/${sessionId}.json`, signal, () => source.session(sessionId!, signal)),
  enabled: !!sessionId,
  staleTime: freshness<RoundFeed>(SESSION_FRESH_MS),
  gcTime: SESSION_FRESH_MS,
  ...staticFile,
});

// Normalised event id -> round, once per rounds array (react-query keeps the array while the rounds do not change).
const roundsByEvent = (() => {
  const cache = new WeakMap<Round[], Map<string, Round>>();
  return (rounds: Round[]) => {
    let byEvent = cache.get(rounds);
    if (!byEvent) {
      byEvent = new Map();
      for (const round of rounds)
        for (const token of ['DPN', 'DPS'] as const) {
          const eventId = normaliseEventId(round.markets?.[token]?.eventId);
          if (eventId && !byEvent.has(eventId)) byEvent.set(eventId, round);
        }
      cache.set(rounds, byEvent);
    }
    return byEvent;
  };
})();

type Titles = string | readonly (string | undefined)[] | undefined;

const useArchivedInfo = (eventId?: string, title?: Titles): ShortRoundInfo | null | undefined => {
  const { feed, checking } = useShortRoundsFeed();
  const key = normaliseEventId(eventId);
  const current = key && feed ? roundsByEvent(feed.rounds).get(key) : undefined;
  // The archive is asked only for a 5-minute title that the current rounds.json does not list, and only once
  // rounds.json has answered (or failed), so a listed round never costs an archive request.
  const wanted = !!key && !checking && !current && [title].flat().some(isShortRoundTitle);
  const index = useQuery({ ...archiveIndexQueryOptions(archiveSource!), enabled: wanted });
  const sessionId = wanted ? index.data?.get(key) : undefined;
  const session = useQuery(archiveSessionQueryOptions(archiveSource!, sessionId));
  if (!key) return null;
  if (current && feed) return { round: current, timezone: feed.timezone };
  if (checking) return undefined;
  if (!wanted) return null;
  if (index.isPending || (sessionId && session.isPending)) return undefined;
  const archived = session.data && roundsByEvent(session.data.rounds).get(key);
  return archived && session.data ? { round: archived, timezone: session.data.timezone } : null;
};
const useNoInfo = (): null => null;

/**
 * The round behind an event id, with the timezone of its times: from the current rounds.json when it lists
 * the event (no extra request), else, for a 5-minute title only, from the archive (index.json, then the
 * session file it names; each read once, however many rows ask). `title` is the event title or market name
 * (or several of them); any one that is an engine round title marks the event as a 5-minute round.
 * undefined while a read is in flight; null for no round: not a 5-minute market, no archive, a missing or
 * malformed file, an unknown event id. Never throws.
 */
export const useShortRoundInfo: (eventId?: string, title?: Titles) => ShortRoundInfo | null | undefined = archiveSource
  ? useArchivedInfo
  : useNoInfo;

/**
 * The rounds of one archived night, as the 5-minute page reads them (ShortMarketPage `night`): a function, so
 * the page never learns where the night comes from.
 */
export type ArchivedNight = { sessionId: string; rounds(signal: AbortSignal): Promise<RoundFeed> };
export const archivedNight = (source: ArchiveSource, sessionId: string, client: QueryClient): ArchivedNight => ({
  sessionId,
  // The entry the sublines read (useShortRoundInfo): a good read stays fresh for SESSION_FRESH_MS, so back and forward
  // between pages never read the file again. The page needs the failure itself (a missing file is final), so a failed
  // read throws here; the sublines' queryFn stores it as null instead.
  rounds: async () => {
    const feed = await client.fetchQuery({
      ...archiveSessionQueryOptions(source, sessionId),
      // A held miss (the sublines' null) is always stale for the page: its own read below returns the real failure.
      staleTime: (query: Query<RoundFeed | null>) => (query.state.data ? SESSION_FRESH_MS : 0),
      queryFn: ({ signal }): Promise<RoundFeed | null> => source.session(sessionId, signal),
    });
    if (!feed) throw new Error('Short-market archive session unavailable (a failed read is held for a minute)');
    return feed;
  },
});

/**
 * True when every round of a session file ended (endAt <= now, seconds). A file with a round that ends later is
 * still running: tonight's (index.json lists tonight too). The status is ignored on purpose: an old night keeps
 * the rounds the engine never settled, and it must stay an archive on the round the reader opened.
 */
export const nightOver = (feed: Pick<RoundFeed, 'rounds'>, now: number) => feed.rounds.every(r => r.endAt <= now);

const useIndexedSession = (eventId: string, wanted: boolean): string | null | undefined => {
  const key = normaliseEventId(eventId);
  // `wanted` starts the read together with rounds.json; a closed gate (rounds.json lists the id) sends nothing.
  const index = useQuery({ ...archiveIndexQueryOptions(archiveSource!), enabled: wanted && !!key });
  if (!wanted || !key) return null;
  // A held miss (null) that is stale is read again right now: wait for that read, not for the old miss.
  if (index.isPending || (index.data === null && index.isFetching)) return undefined;
  return index.data?.get(key) ?? null;
};
const useNoSession = (): null => null;

/**
 * The session (night) that index.json lists an event in, for the event gate. undefined while the index read is
 * in flight (at most FEED_TIMEOUT_MS); null for no night: `wanted` false, no archive, a missing or malformed
 * index, an unknown event id. Never throws. The index also lists tonight's events: the caller reads it together
 * with rounds.json, but uses its answer only after rounds.json answered without listing the id.
 */
export const useArchivedSession: (eventId: string, wanted: boolean) => string | null | undefined = archiveSource
  ? useIndexedSession
  : useNoSession;
